import { BaseStorage } from "@/storage/BaseStorage";
import { NetLogicOrder } from "./NetLogicOrder";
import { NetLogicTableConfiguration } from "@/core/NetLogicTableConfiguration";
import { NetLogicAPI } from "@/api/NetLogicAPI";
import Bugsnag from "@bugsnag/js";
import { TableItem } from "../TableItem";
import { TableOverview, TableStatus } from "../Table";
import { CatalogHelpers } from "@/tools";
import { UnavailableProduct } from "../expections/UnavailableProduct";
import { ClosingOrderTenderMedia } from "./order/ClosingOrderTenderMedia";
import { CallingWaiterOrder } from "./order/CallingWaiterOrder";
import { OrderAPI } from "@/api/OrderAPI";

export class NetLogicTable {
  constructor() {
    this.netLogicConfiguration = NetLogicTableConfiguration.fromStorage();

    Bugsnag.leaveBreadcrumb(
      "Configurações da NetLogic",
      this.netLogicConfiguration.export()
    );

    const tableNumber = this.netLogicConfiguration.externalId;
    this.storage = new BaseStorage({ key: `of-netlogic-table-${tableNumber}` });

    this.client = new NetLogicAPI({
      baseUrl: this.netLogicConfiguration.apiUrl,
      apiToken: this.netLogicConfiguration.apiToken,
    });
  }

  async getStatus() {
    await this.syncronize();

    const netLogicOrderRaw = this.storage.getData();

    if (!netLogicOrderRaw?.checkNumber) {
      return TableStatus.AVAILABLE;
    }

    if (netLogicOrderRaw?.preClosed) {
      return TableStatus.PRECLOSED;
    }

    return TableStatus.OPENED;
  }

  async syncronize() {
    await this.syncronizeTable();
  }

  async syncronizeTable() {
    const netLogicOrderRaw = this.storage.getData() || {};

    const tableNum = this.netLogicConfiguration.externalId;
    // const employeeId = Number(this.netLogicConfiguration.orderEmployee);
    const netLogicOrderResponse = await this.client.getOrderByTableNum({
      tableNum,
      employeeId: 0
    });

    if (netLogicOrderResponse.getErrors([]).length > 0) {
      console.log(
        `Falha ao consultar a mesa ${tableNum} - ${
          netLogicOrderResponse.getErrors()[0]
        }`
      );

      throw new Error(`Não foi possível consultar a mesa ${tableNum}`);
    }

    const netLogicOrder = netLogicOrderResponse.getData({});

    let checkNumber = netLogicOrder?.Summary?.CheckNumber;
    let checkSeq = netLogicOrder?.Summary?.CheckSeqNumber;
    let checkId = netLogicOrder?.Summary?.CheckId;

    if (!checkNumber && !checkSeq && Array.isArray(netLogicOrder || [])) {
      const sortedOrders = netLogicOrder.sort((prevOrder, nextOrder) => {
        const prevDate = new Date(prevOrder.CheckOpenTime);
        const nextDate = new Date(nextOrder.CheckOpenTime);
        return prevDate - nextDate;
      });

      const mainOrder = sortedOrders[0];
      checkNumber = mainOrder.Summary?.CheckNumber;
      checkSeq = mainOrder.Summary?.CheckSeqNumber;
      checkId = mainOrder.Summary?.CheckId;
    }

    if (!checkSeq || !checkNumber) {
      this.storage.setData(null);
      console.log(`A mesa ${tableNum} não possui um pedido aberto`)
      return null;
    }

    netLogicOrderRaw.checkNumber = checkNumber;
    netLogicOrderRaw.checkSeq = checkSeq;
    netLogicOrderRaw.checkId = checkId;

    return this.storage.setData(netLogicOrderRaw);
  }

  async addItems(order) {
    await this.syncronize();

    const currentNetLogicRawOrder = this.storage.getData() || {};

    const netlogicSku = new Map();

    CatalogHelpers.executeOnSchema(order?.cart?.catalog, (item, parent) => {
      const sku = item?.sku;

      const name = item.type === "size" ? parent.name : item.name;

      if (sku && !netlogicSku.has(sku)) {
        netlogicSku.set(sku, {
          name,
          sku,
        });
      }
    });

    for (const item of netlogicSku.values()) {
      const availabilityResponse = await this.client.getProductAvailability(
        item.sku
      );
      const availability = availabilityResponse.getData({});

      const outOfMenuItem = availability?.menuitem?.outOfMenuItem;

      if (outOfMenuItem === true) {
        throw new UnavailableProduct({
          sku: item.sku,
          name: item.name,
        });
      }
    }

    const revenueCenter = String(this.netLogicConfiguration.revenueCenter || 0) === "0"
      ? 1 
      : Number(this.netLogicConfiguration.revenueCenter);

    const orderConverter = new NetLogicOrder({
      checkSeq: currentNetLogicRawOrder?.checkSeq,
      checkNum: currentNetLogicRawOrder?.checkNumber,
      checkId: currentNetLogicRawOrder?.checkId,
      tableNum: this.netLogicConfiguration.externalId,
      type: Number(this.netLogicConfiguration.orderType),
      revenueCenter,
      employeeObjectNum: Number(this.netLogicConfiguration.orderEmployee),
      tenderMedia: String(this.netLogicConfiguration.tenderMediaIncrement),
      ...order,
    });

    const convertedOrder = orderConverter.export();

    Bugsnag.leaveBreadcrumb("Conversão do pedido", {
      original: order,
      converted: convertedOrder,
    });

    const updatedOrderResponse = await this.client.createOrder(convertedOrder);

    if (updatedOrderResponse.getErrors([]).length > 0) {
      Bugsnag.notify(new Error(updatedOrderResponse.getErrors([])[0]));
      throw new Error("Não foi possível incluir os itens na mesa");
    }

    const updatedOrder = updatedOrderResponse.getData({});

    this.storage.setData({
      checkSeq: updatedOrder.checkSeq,
      checkNumber: updatedOrder.checkNumber,
      checkId: updatedOrder.checkId,
    });
  }

  async callWaiter() {
    await this.syncronize();

    const currentNetLogicRawOrder = this.storage.getData() || {};

    const closingStrategy = new CallingWaiterOrder({
      checkNumber: currentNetLogicRawOrder.checkNumber,
      checkSeq: currentNetLogicRawOrder.checkSeq,
      checkId: currentNetLogicRawOrder.checkId,
      tableNum: this.netLogicConfiguration.externalId,
      type: Number(this.netLogicConfiguration.orderType),
      employeeObjectNum: Number(this.netLogicConfiguration.orderEmployee),
      payersQuantity: null,
      tenderMedia: Number(this.netLogicConfiguration.tenderMediaIncrement),
      callWaiterSku: this.netLogicConfiguration.callWaiterSku,
    });

    const closingOrder = closingStrategy.export();

    Bugsnag.leaveBreadcrumb("Solicitação de garçom", closingOrder);

    
    const updatedOrderResponse = await this.client.createOrder(closingOrder);

    if (updatedOrderResponse.getErrors([]).length > 0) {
      Bugsnag.notify(new Error(updatedOrderResponse.getErrors([])[0]));
      throw new Error("Não foi possível chamar o garçom");
    }
  }

  async close({ payersQuantity }) {
    const status = await this.getStatus();

    if (status != TableStatus.OPENED) {
      throw new Error("Fechamento indisponível");
    }

    const currentNetLogicRawOrder = this.storage.getData() || {};
    const callWaiterSku = this.netLogicConfiguration.callWaiterClosingSku || this.netLogicConfiguration.callWaiterSku;

    const closingStrategy = new ClosingOrderTenderMedia({
      checkNumber: currentNetLogicRawOrder.checkNumber,
      checkSeq: currentNetLogicRawOrder.checkSeq,
      checkId: currentNetLogicRawOrder.checkId,
      tableNum: this.netLogicConfiguration.externalId,
      type: Number(this.netLogicConfiguration.orderType),
      employeeObjectNum: Number(this.netLogicConfiguration.orderEmployee),
      payersQuantity: payersQuantity ? +payersQuantity : null,
      tenderMedia: Number(this.netLogicConfiguration.tenderMediaClosing),
      callWaiterSku: callWaiterSku,
    });

    const closingOrder = closingStrategy.export();

    Bugsnag.leaveBreadcrumb("Solicitação do fechamento", closingOrder);

    const updatedOrderResponse = await this.client.createOrder(closingOrder);

    if (updatedOrderResponse.getErrors([]).length > 0) {
      Bugsnag.notify(new Error(updatedOrderResponse.getErrors([])[0]));
      throw new Error("Não foi possível solicitar o fechamento da mesa");
    }

    await OrderAPI.getTableTracking();

    this.storage.setData({
      checkSeq: currentNetLogicRawOrder.checkSeq,
      checkNumber: currentNetLogicRawOrder.checkNumber,
      checkId: currentNetLogicRawOrder.checkId,
      guestCount: payersQuantity,
      preClosed: true,
    });
  }

  async getOverview() {
    const status = await this.getStatus();

    const netLogicOrderRaw = this.storage.getData();

    if (!netLogicOrderRaw?.checkNumber) {
      return null;
    }

    // const employeeId = Number(this.netLogicConfiguration.orderEmployee);
    const netlogicOrderResponse = await this.client.getOrderByCheckNumber({
      checkNumber: netLogicOrderRaw.checkNumber,
      employeeId: 0,
    });

    const netLogicOrders = netlogicOrderResponse.getData([]);
    let netLogicOrder = null;
    if (Array.isArray(netLogicOrders)) {
      netLogicOrder = netLogicOrders.find((order) => order?.Summary?.CheckNumber === netLogicOrderRaw.checkNumber)
    } else {
      netLogicOrder = netLogicOrders;
    }
    

    const subtotal = +(netLogicOrder?.Summary?.SubTotal || 0);
    const fee = +(netLogicOrder?.Summary?.AutoGratuity || 0);
    const total = +(netLogicOrder?.Summary?.Due || 0);

    let detailLines = netLogicOrder?.DetailLines?.DetailLine || [];

    if (!Array.isArray(detailLines)) {
      detailLines = [detailLines];
    }

    const ItemOperations = {
      CONSUME: "CONSUME",
      CANCELATION: "CANCELATION",
    };

    const getItemType = (item) => {
      if ((item?.SalesCount || 0) < 0) {
        return ItemOperations.CANCELATION;
      }

      return ItemOperations.CONSUME;
    };

    const menuItems = detailLines
      .filter((line) => line?.MenuItem)
      .map((detailLine) => {
        const type = getItemType(detailLine);

        return {
          id: String(detailLine?.DetailLink),
          sku: String(detailLine?.MenuItem?.obj),
          parent: String(detailLine?.ParentDetailLink),
          cancelationItemId:
            type === ItemOperations.CANCELATION
              ? String(detailLine?.VoidLink)
              : null,
          type,
          sort: detailLine?.RoundNumber || 0,
          name: detailLine?.MenuItem?.Name,
          quantity: +(detailLine?.SalesCount || 0),
          unitPrice: null,
          totalPrice: +(detailLine?.Total || 0),
        };
      });

    const canceledItems = menuItems
      .filter((item) => item.type === ItemOperations.CANCELATION)
      .map((item) => item.cancelationItemId);

    const parentItens = menuItems.filter((item) => {
      // if (canceledItems.includes(item.id)) {
      //   console.log("O item %s - %s foi cancelado", item.id, item.name);
      // }

      // Verifica tanto o SKU padrão quanto o SKU de fechamento
      const callWaiterSku = this.netLogicConfiguration.callWaiterSku
        ? this.netLogicConfiguration.callWaiterSku.slice(1)
        : null;
      const callWaiterClosingSku = this.netLogicConfiguration.callWaiterClosingSku
        ? this.netLogicConfiguration.callWaiterClosingSku.slice(1)
        : null;
      const isClosingOrderItem =
        (item.sku === callWaiterSku || item.sku === callWaiterClosingSku) && item.totalPrice <= 0.01;
      if (isClosingOrderItem) {
        return false;
      }

      return (
        item.parent === "0" &&
        item.type === ItemOperations.CONSUME &&
        !canceledItems.includes(item.id)
      );
    });

    const items = [];
    for (const parent of parentItens) {
      const childrenItens = menuItems.filter(
        (children) => children.parent == parent.id
      );

      parent.items = [];

      const mergedItem = [parent, ...childrenItens];
      items.push(...mergedItem);      
    }

    return new TableOverview({
      subtotal,
      total,
      fee,
      status,
      items: items.map((p) => new TableItem(p)),
      payersQuantity: netLogicOrderRaw.guestCount,
    });
  }
}
